// Tap to talk — T57 (10 Sep 2026), agents doc § 7.4: the engines behind the kadmon panel's mic.
//
// SPEECH IS A TRANSPORT (CLAUDE.md § What kadmon is). The recruiter's words become text in the
// panel's composer, and the text is what kadmon is asked; her answer is text, read aloud. The
// assistant stays one text agent with tools, and nothing in this file is a second agent.
//
// WHO HEARS THE AUDIO — the data question § 7.4 left open. A browser's recognition sends the audio
// to the browser vendor's service: Chrome's to Google, a party kadmon has no agreement with. So the
// input is an interface, `SpeechInput`, with two engines behind it: the browser's, below, for local
// trials on invented data; and Amazon Transcribe streaming, in eu-west-1 on kadmon's own account —
// Natanel's choice on T57's measurement (10 Sep 2026), built at T57b in src/transcribe.ts, and the
// deployed brain's (`VITE_VOICE_INPUT`, src/env.ts). Each says who hears the audio, and the
// panel tells the recruiter every time the mic opens.
//
// HER VOICE (T66, 13 Sep 2026). Two outputs behind `SpeechOutput`. The browser's own, below, reads
// with a voice the browser marks as local — Chrome's "Google" voices and Edge's "Online (Natural)"
// ones are network voices, whose vendor would receive the answer's text, candidate names included
// — and was the deployed brain's until T66: it sounds like a machine and reads a slash as
// "slash". The deployed brain's since T66 is Amazon Polly (`pollySpeechOutput`), through the
// brain API on kadmon's own account, the same footing as Transcribe hearing the recruiter:
// Natanel's decision, so the answer's text does leave the device now — to AWS, and nobody else.
// Either way `spokenText` first rewrites the answer for the ear, and no voice, or no audio, means
// no spoken reply — the answer is on screen either way.

import { silenceWatcher, type FinishedAsk, type ListenMode } from './silence.ts';

/** The language the recruiter is heard in. The brain is English, and so are its recruiters' asks. */
export const SPEECH_LANG = 'en-GB';

/**
 * Silence that ends a dictation: this long with no new word from the engine. The engine runs
 * CONTINUOUS and this module ends it, because Chrome's one-shot mode ends at its own pause and
 * keeps only the last phrase — "create a candidate named Anna Virtanen, number +358…" came back as
 * "number +358-401-233-4567" (measured 10 Sep 2026, `pnpm console:speech`). Long enough for a
 * breath between the groups of a phone number; short enough that nobody waits on it.
 */
export const SILENCE_MS = 2_000;
/** A mic tapped open and never spoken into closes after this, saying it heard nothing. */
export const NOTHING_HEARD_MS = 8_000;
/** No open mic (§ 7.4): however long the recruiter talks, the session ends here. */
export const MAX_LISTEN_MS = 60_000;
/** After a stop, how long the engine has to hand over its last words before this module ends for it. */
export const END_GRACE_MS = 3_000;

/** What a dictation came to: the words, or the engine's reason there are none. */
export type Heard = { text: string } | { error: string };

export interface ListenHandlers {
  /** The transcript so far, words the engine may still change included. Called as it changes. */
  onText(text: string): void;
  /** Exactly once — unless the session was aborted, when it is never called. */
  onEnd(heard: Heard): void;
}

export interface ListenOptions {
  /**
   * Audio to recognise instead of the microphone. Only `pnpm console:speech` passes one: it plays
   * each recorded clip to the same code the panel listens with. Chrome's own fake microphone
   * (`--use-file-for-fake-audio-capture`) reaches getUserMedia and never the recogniser, which
   * heard sound and returned no words on every try (10 Sep 2026).
   */
  source?: AudioTrackLike;
  /** Another language to hear — the measurement's `--lang`; the panel always uses `SPEECH_LANG`. */
  lang?: string;
  /**
   * Silence that ends this listen, instead of `SILENCE_MS` — voice mode's shorter rule (T66a,
   * `VOICE_SILENCE_MS`), where a two-second pause would be most of what makes a conversation slow.
   */
  silenceMs?: number;
  /**
   * What the words are for (T451c): 'dictation' (the default), which `finished` reads, or the
   * backup Talk's 'talk', whose fixed silence is its whole rule (silence.ts).
   */
  mode?: ListenMode;
}

/**
 * A `MediaStreamTrack`, as far as this module needs one. Structural, so the module names no DOM
 * type and the measurement script can read `SPEECH_LANG` from it under Node.
 */
export interface AudioTrackLike {
  readonly kind: string;
}

export interface Listening {
  /** The second tap: finish now, with what has been heard. */
  stop(): void;
  /** Tear down without a word — the panel closed, or another chat was picked. */
  abort(): void;
}

/** Tap to talk's input, behind which the engine is a choice (§ 7.4). */
export interface SpeechInput {
  /**
   * Who hears the audio, as the line under the composer says it while the mic is open — "your
   * browser’s speech service hears this", or Transcribe's (T57b). The recruiter is told every time.
   */
  readonly whoHears: string;
  listen(handlers: ListenHandlers, options?: ListenOptions): Listening;
}

/** An answer arriving a sentence at a time (T66b): push each, then say it is whole. */
export interface SpeechSink {
  push(text: string): void;
  /** No more sentences: `done` follows once the last one has been read. */
  end(): void;
}

/** Tap to talk's output: kadmon's answer, read aloud. */
export interface SpeechOutput {
  /** Read `text`, then call `done` — at once when there is no local voice to read it in. */
  speak(text: string, done: () => void): void;
  /**
   * Read an answer as it is written (T66b, voice mode): sentences pushed as the model writes
   * them, read in order as they come, `done` once the last has been read after `end`. A cancel,
   * or a newer speak or stream, retires the sink: anything pushed after is dropped.
   */
  stream(done: () => void): SpeechSink;
  cancel(): void;
  /**
   * Called inside the tap that opens the mic. iOS Safari reads aloud later only if something was
   * spoken inside a gesture first, and the answer arrives seconds after the gesture.
   */
  prime(): void;
}

// --- the input -----------------------------------------------------------------------------

/** One alternative, as the Web Speech API hands it over; a result's first is the engine's best. */
interface AlternativeLike {
  readonly transcript: string;
}

/** One stretch of speech: its alternatives, and whether the engine may still change it. */
export interface ResultLike {
  readonly isFinal: boolean;
  readonly length: number;
  readonly [index: number]: AlternativeLike | undefined;
}

export interface ResultListLike {
  readonly length: number;
  readonly [index: number]: ResultLike | undefined;
}

/** The part of `SpeechRecognition` this module uses. TypeScript's DOM library does not declare it. */
interface RecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  onresult: ((event: { readonly results: ResultListLike }) => void) | null;
  onerror: ((event: { readonly error: string }) => void) | null;
  onend: (() => void) | null;
  start(source?: AudioTrackLike): void;
  stop(): void;
  abort(): void;
}

type RecognitionConstructor = new () => RecognitionLike;

/**
 * Every result the session holds, as one line: finals and the words still being decided, in
 * order. Chrome on Android is reported to repeat a continuous session's finals, each carrying the
 * one before it — "call", "call Anna", "call Anna now" (reported, not measured: this lane had no
 * Android device) — so a result that is the one before it plus more words replaces it. Whole
 * words only: "number" then "numbers matter" are two phrases, not a repeat.
 */
export function joinResults(results: ResultListLike): string {
  const parts: string[] = [];
  for (let i = 0; i < results.length; i += 1) {
    const text = (results[i]?.[0]?.transcript ?? '').replace(/\s+/g, ' ').trim();
    if (text === '') continue;
    const last = parts[parts.length - 1]?.toLowerCase();
    const lower = text.toLowerCase();
    if (last !== undefined && (lower === last || lower.startsWith(`${last} `))) {
      parts[parts.length - 1] = text;
    } else {
      parts.push(text);
    }
  }
  return parts.join(' ');
}

/** Who hears a dictation on the browser's own engine: its vendor's service — Chrome's is Google's. */
export const BROWSER_HEARS = 'your browser’s speech service hears this';

/**
 * The browser's own recognition, or null where it has none — Firefox, and any browser that
 * hides it — in which case the panel draws no mic, because a dead button is worse than none.
 */
export function browserSpeechInput(scope: object, finished?: FinishedAsk): SpeechInput | null {
  const { SpeechRecognition, webkitSpeechRecognition } = scope as {
    SpeechRecognition?: RecognitionConstructor;
    webkitSpeechRecognition?: RecognitionConstructor;
  };
  const Recognition = SpeechRecognition ?? webkitSpeechRecognition;
  if (typeof Recognition !== 'function') return null;

  return {
    whoHears: BROWSER_HEARS,
    listen(handlers, options = {}) {
      const recognition = new Recognition();
      recognition.lang = options.lang ?? SPEECH_LANG;
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;

      let text = '';
      let error: string | null = null;
      let over = false;
      let stopping = false;
      let quiet: ReturnType<typeof setTimeout> | null = null;
      let cap: ReturnType<typeof setTimeout> | null = null;

      const detach = () => {
        over = true;
        watcher.clear();
        if (quiet !== null) clearTimeout(quiet);
        if (cap !== null) clearTimeout(cap);
        recognition.onresult = null;
        recognition.onerror = null;
        recognition.onend = null;
      };
      const finish = () => {
        if (over) return;
        detach();
        handlers.onEnd(text === '' ? { error: error ?? 'no-speech' } : { text });
      };
      // T451c: the fixed silence, or a model's read of the words when the app gave one (silence.ts).
      const watcher = silenceWatcher({
        silenceMs: options.silenceMs ?? SILENCE_MS,
        mode: options.mode ?? 'dictation',
        finished,
        stop: () => stop(),
      });
      const wait = (ms: number, then: () => void) => {
        if (quiet !== null) clearTimeout(quiet);
        quiet = setTimeout(then, ms);
      };
      const stop = () => {
        if (over || stopping) return;
        stopping = true;
        watcher.clear();
        recognition.stop();
        // The engine owes its last words and an `end`. If they never come, end for it.
        wait(END_GRACE_MS, finish);
      };

      recognition.onresult = (event) => {
        text = joinResults(event.results);
        handlers.onText(text);
        if (!stopping) {
          if (quiet !== null) clearTimeout(quiet);
          watcher.heard(text);
        }
      };
      recognition.onerror = (event) => {
        error = event.error;
      };
      recognition.onend = finish;

      wait(NOTHING_HEARD_MS, stop);
      cap = setTimeout(stop, MAX_LISTEN_MS);
      try {
        if (options.source) recognition.start(options.source);
        else recognition.start();
      } catch {
        // Refused before it began — a second session in the tab, say. Said, not thrown at the tap.
        error = 'start-failed';
        finish();
      }

      return {
        stop,
        abort() {
          if (over) return;
          detach();
          recognition.abort();
        },
      };
    },
  };
}

/** Why a dictation produced nothing, in a sentence a recruiter can act on; null when they chose it. */
export function explainSpeechError(code: string): string | null {
  switch (code) {
    case 'aborted':
      return null;
    case 'no-speech':
      return 'Nothing was heard. Tap the mic and speak, or type instead.';
    case 'audio-capture':
      return 'No microphone was found. Connect one, or type instead.';
    case 'not-allowed':
    case 'service-not-allowed':
      return 'This browser may not use the microphone here. Allow it from the address bar, or type instead.';
    case 'network':
      return 'The browser’s speech service did not answer. Try again, or type instead.';
    case 'language-not-supported':
      return 'This browser cannot hear English speech. Type instead.';
    // Amazon Transcribe's (T57b, src/transcribe.ts): the brain got no URL to open, the stream
    // was refused or dropped, or every stream the account may run is in use.
    case 'transcribe-unavailable':
      return 'The brain could not start Amazon Transcribe. Try again, or type instead.';
    case 'transcribe-failed':
      return 'Amazon Transcribe did not answer. Try again, or type instead.';
    case 'transcribe-busy':
      return 'Amazon Transcribe is busy. Try again in a moment, or type instead.';
    default:
      return `The browser could not listen (${code}). Type instead.`;
  }
}

/** What was heard, after whatever the recruiter had already typed. */
export const appendHeard = (before: string, heard: string): string =>
  [before.trim(), heard.trim()].filter((part) => part !== '').join(' ');

// --- the output ----------------------------------------------------------------------------

/** What a voice says in place of a link or an address: they are on screen, and unsayable. */
export const LINK_SPOKEN = 'the link on screen';
export const EMAIL_SPOKEN = 'the address on screen';

/**
 * An answer as a voice should say it — T66 (13 Sep 2026). kadmon writes for the screen: a link, an
 * address, "London/Stockholm", "+358401234567", a list with bullets and bold. A voice reading that
 * verbatim says "slash", "asterisk", "h t t p" and a phone number as one enormous number, which
 * is what Natanel heard. So, before either voice: markdown goes (bold, italic, code, headings,
 * bullets — a bullet becomes a sentence); a link or an email is replaced by what is on screen; a
 * slash between words is "or"; a number in E.164 is said a digit at a time, in the groups a
 * person uses; a dash is the comma it is read as; a bracketed aside keeps its words. The text on
 * screen is untouched — this is the spoken copy.
 */
export function spokenText(text: string): string {
  return (
    text
      // Fenced or inline code: the words inside stay, the ticks go.
      .replace(/```[a-z]*\n?([\s\S]*?)```/g, '$1')
      .replace(/`([^`]*)`/g, '$1')
      // A markdown link keeps its words and loses its target; a bare link is what is on screen.
      .replace(/\[([^\]]+)\]\((?:https?:\/\/|mailto:)[^)]*\)/g, '$1')
      .replace(/\bhttps?:\/\/\S+/g, LINK_SPOKEN)
      .replace(/\bwww\.\S+/g, LINK_SPOKEN)
      .replace(/\b[\w.+-]+@[\w-]+(?:\.[\w-]+)+\b/g, EMAIL_SPOKEN)
      // Headings, bullets and numbered items: each becomes its own sentence.
      .replace(/^\s{0,3}#{1,6}\s+/gm, '')
      .replace(/^\s*(?:[-*•]|\d+[.)])\s+/gm, '')
      // Bold, italic and strikethrough marks; a stray asterisk or underscore pair.
      .replace(/(\*\*|__)(.*?)\1/g, '$2')
      .replace(/(?<![\w])[*_]([^*_\n]+)[*_](?![\w])/g, '$1')
      .replace(/~~(.*?)~~/g, '$1')
      // A phone number in E.164, digits in groups of three, said one at a time.
      .replace(/\+(\d{7,15})\b/g, (_m, digits: string) => `plus ${spokenDigits(digits)}`)
      // "London/Stockholm" is "London or Stockholm"; "and/or" is already "and or".
      .replace(/(?<=[A-Za-zÀ-ÿ])\s*\/\s*(?=or\b)/g, ' ')
      .replace(/(?<=[A-Za-zÀ-ÿ])\s*\/\s*(?=[A-Za-zÀ-ÿ])/g, ' or ')
      // What is left of a slash, a pipe or an angle bracket is a pause.
      .replace(/\s*[/|<>]\s*/g, ', ')
      // A bracketed aside keeps its words; the brackets themselves are not read.
      .replace(/\s*[[\]{}]\s*/g, ' ')
      .replace(/\(\s*/g, ', ')
      .replace(/\s*\)/g, ',')
      // A dash is the pause it is read as; a line break ends a sentence.
      .replace(/\s*[—–]\s*/g, ', ')
      .replace(/\s*\n+\s*/g, (m) => (m.includes('\n') ? '. ' : m))
      // Tidy: doubled pauses, a comma before a full stop, runs of space.
      .replace(/(?:,\s*){2,}/g, ', ')
      .replace(/,\s*([.!?])/g, '$1')
      .replace(/\.\s*\.\s*/g, '. ')
      .replace(/\s+/g, ' ')
      .replace(/\s+([,.!?])/g, '$1')
      .trim()
  );
}

/** "358401234567" as "3 5 8, 4 0 1, 2 3 4, 5 6 7": one digit at a time, a breath every three. */
function spokenDigits(digits: string): string {
  const groups: string[] = [];
  for (let i = 0; i < digits.length; i += 3)
    groups.push(
      digits
        .slice(i, i + 3)
        .split('')
        .join(' '),
    );
  return groups.join(', ');
}

/** The fields of `SpeechSynthesisVoice` this module reads. */
export interface VoiceLike {
  readonly name: string;
  readonly lang: string;
  readonly localService: boolean;
  readonly default?: boolean;
}

/**
 * kadmon's voice (§ 7.4, "one chosen voice"): a British English voice on the recruiter's own
 * device, the first of these the browser has — Apple's, then Windows' own, as browsers name them
 * ("Microsoft Hazel - English (United Kingdom)" starts with "Microsoft Hazel"). Only LOCAL voices
 * are ever considered; a platform with none of these gets its first local en-GB voice, then its
 * first local English one, then no spoken reply.
 */
export const VOICE_PREFERENCE: readonly string[] = [
  'Serena',
  'Kate',
  'Martha',
  'Stephanie',
  'Microsoft Libby',
  'Microsoft Sonia',
  'Microsoft Hazel',
  'Microsoft Susan',
];

export function pickVoice(voices: readonly VoiceLike[]): VoiceLike | null {
  const local = voices.filter((voice) => voice.localService);
  for (const name of VOICE_PREFERENCE) {
    const hit = local.find((voice) => voice.name === name || voice.name.startsWith(`${name} `));
    if (hit) return hit;
  }
  const lang = (voice: VoiceLike) => voice.lang.replace('_', '-').toLowerCase();
  const first = (list: readonly VoiceLike[]) => list.find((voice) => voice.default) ?? list[0];
  return (
    first(local.filter((voice) => lang(voice) === 'en-gb')) ??
    first(local.filter((voice) => lang(voice).startsWith('en'))) ??
    null
  );
}

/** Where an answer is cut for reading: a sentence at a time, and no piece longer than this. */
export const SPEECH_CHUNK = 180;

/**
 * An answer as the pieces it is read in. A sentence at a time, so Stop is prompt and a long
 * answer is not one utterance — Chrome is widely reported to cut a long one off mid-word (not
 * measured here); a sentence past `max` is cut at its last comma, else its last space. A dash
 * becomes a comma, which is how it is read.
 */
export function chunkForSpeech(text: string, max = SPEECH_CHUNK): string[] {
  const clean = text
    .replace(/\s*[—–]\s*/g, ', ')
    .replace(/\s+/g, ' ')
    .trim();
  if (clean === '') return [];
  const chunks: string[] = [];
  for (const sentence of clean.split(/(?<=[.!?…])\s+/)) {
    let rest = sentence;
    while (rest.length > max) {
      const window = rest.slice(0, max + 1);
      const comma = window.lastIndexOf(', ');
      const space = window.lastIndexOf(' ');
      const at = comma > 0 ? comma + 1 : space > 0 ? space : max;
      chunks.push(rest.slice(0, at).trim());
      rest = rest.slice(at).trim();
    }
    if (rest !== '') chunks.push(rest);
  }
  return chunks;
}

/** The fields of `SpeechSynthesisUtterance` this module sets. */
interface UtteranceLike {
  text: string;
  lang: string;
  voice: VoiceLike | null;
  volume: number;
  onend: (() => void) | null;
  onerror: (() => void) | null;
}

type UtteranceConstructor = new (text: string) => UtteranceLike;

interface SynthesisLike {
  speak(utterance: UtteranceLike): void;
  cancel(): void;
  getVoices(): VoiceLike[];
  addEventListener?(type: 'voiceschanged', listener: () => void): void;
  removeEventListener?(type: 'voiceschanged', listener: () => void): void;
}

/** How long a first answer waits for Chrome to fill its voice list before it is not read. */
export const VOICES_WAIT_MS = 1_500;

/** The browser's own speech synthesis, or null where it has none. */
export function browserSpeechOutput(scope: object): SpeechOutput | null {
  const { speechSynthesis: synthesis, SpeechSynthesisUtterance: Utterance } = scope as {
    speechSynthesis?: SynthesisLike;
    SpeechSynthesisUtterance?: UtteranceConstructor;
  };
  if (!synthesis || typeof Utterance !== 'function') return null;

  // A cancel, or a newer answer, retires every callback of the reading before it.
  let turn = 0;
  const withVoices = (then: (voices: readonly VoiceLike[]) => void) => {
    const now = synthesis.getVoices();
    if (now.length > 0 || !synthesis.addEventListener) {
      then(now);
      return;
    }
    // Chrome fills the list after it is first asked for, and says so with `voiceschanged`.
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      synthesis.removeEventListener?.('voiceschanged', settle);
      then(synthesis.getVoices());
    };
    synthesis.addEventListener('voiceschanged', settle);
    setTimeout(settle, VOICES_WAIT_MS);
  };

  return {
    speak(text, done) {
      turn += 1;
      const mine = turn;
      synthesis.cancel();
      withVoices((voices) => {
        if (mine !== turn) return;
        const voice = pickVoice(voices);
        const chunks = chunkForSpeech(text);
        if (voice === null || chunks.length === 0) {
          done();
          return;
        }
        chunks.forEach((chunk, i) => {
          const utterance = new Utterance(chunk);
          utterance.voice = voice;
          utterance.lang = voice.lang;
          if (i === chunks.length - 1) {
            const end = () => {
              if (mine === turn) done();
            };
            utterance.onend = end;
            utterance.onerror = end;
          }
          synthesis.speak(utterance);
        });
      });
    },
    stream(done) {
      turn += 1;
      const mine = turn;
      synthesis.cancel();
      // Each sentence is queued on the synthesis as it arrives; the last one's end is `done`,
      // known only once `end` has been called — so every utterance reports, and the report
      // counts against what has been pushed.
      let voice: VoiceLike | null | undefined;
      let pushed = 0;
      let finished = 0;
      let ended = false;
      const settle = () => {
        if (mine === turn && ended && finished >= pushed) done();
      };
      const pending: string[] = [];
      const say = (chunk: string) => {
        if (voice === null) {
          finished += 1;
          settle();
          return;
        }
        const utterance = new Utterance(chunk);
        utterance.voice = voice!;
        utterance.lang = voice!.lang;
        const report = () => {
          finished += 1;
          settle();
        };
        utterance.onend = report;
        utterance.onerror = report;
        synthesis.speak(utterance);
      };
      withVoices((voices) => {
        if (mine !== turn) return;
        voice = pickVoice(voices);
        for (const chunk of pending.splice(0)) say(chunk);
        settle();
      });
      return {
        push(text) {
          if (mine !== turn) return;
          for (const chunk of chunkForSpeech(text)) {
            pushed += 1;
            if (voice === undefined) pending.push(chunk);
            else say(chunk);
          }
        },
        end() {
          if (mine !== turn) return;
          ended = true;
          if (voice !== undefined) settle();
        },
      };
    },
    cancel() {
      turn += 1;
      synthesis.cancel();
    },
    prime() {
      const utterance = new Utterance(' ');
      utterance.volume = 0;
      synthesis.speak(utterance);
    },
  };
}

// --- her voice: Polly, through the brain API (T66, 13 Sep 2026) --------------------------

/** The part of `HTMLAudioElement` this module drives. One element, made once and reused. */
export interface AudioLike {
  src: string;
  onended: (() => void) | null;
  onerror: (() => void) | null;
  play(): Promise<void> | void;
  pause(): void;
}

/** Blob URLs, handed over so the module names no DOM and a test needs none. */
export interface ObjectUrls {
  create(bytes: ArrayBuffer): string;
  revoke(url: string): void;
}

export interface PollyParts {
  /** One sentence's audio from the service (`speechApi(...).say`). Rejects when it cannot be said. */
  say(text: string): Promise<ArrayBuffer>;
  /**
   * The one audio element. iOS unlocks playback per element and only inside a gesture, so
   * `prime` plays silence on it in the tap, and every answer after plays on the same element.
   */
  audio: AudioLike;
  urls: ObjectUrls;
}

/**
 * A silent WAV, forty-four bytes of header and a few of nothing, as a data URL — what `prime`
 * plays inside the tap. Built here rather than pasted, so it is right by construction.
 */
export function silentWav(): string {
  const samples = 8;
  const bytes = new Uint8Array(44 + samples * 2);
  const view = new DataView(bytes.buffer);
  const ascii = (at: number, text: string) => {
    for (let i = 0; i < text.length; i += 1) bytes[at + i] = text.charCodeAt(i);
  };
  ascii(0, 'RIFF');
  view.setUint32(4, 36 + samples * 2, true);
  ascii(8, 'WAVE');
  ascii(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, 8_000, true);
  view.setUint32(28, 16_000, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  ascii(36, 'data');
  view.setUint32(40, samples * 2, true);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return `data:audio/wav;base64,${btoa(binary)}`;
}

/**
 * Her voice: each sentence of the answer fetched from the service as Polly says it, played in
 * order on the one element — the next sentence's audio asked for while this one plays, so the
 * gap between sentences is the fetch of neither. A sentence at a time, uncut (`chunkForSpeech`
 * with no limit), because Polly's phrasing is per request and a cut mid-sentence is a pause where
 * none belongs. The first sentence starts as soon as its audio is back — the whole answer is not
 * waited for. Anything that fails — no credentials on the service, a refusal, a blob the element
 * will not play — ends the reading at once: the answer is on screen, and nothing is retried.
 */
export function pollySpeechOutput(parts: PollyParts): SpeechOutput {
  const { audio, urls } = parts;
  let turn = 0;
  let playing: string | null = null;

  const stop = () => {
    audio.onended = null;
    audio.onerror = null;
    audio.pause();
    if (playing !== null) {
      urls.revoke(playing);
      playing = null;
    }
  };

  /**
   * The reading: a list of sentences that may still grow (a stream) or is whole (a speak), each
   * sentence's audio asked for once and one ahead of the one playing, played in order on the
   * one element. `done` once the last has played and the list is closed.
   */
  const reading = (done: () => void) => {
    turn += 1;
    const mine = turn;
    stop();
    const sentences: string[] = [];
    let closed = false;
    let at = 0;
    let waiting = false;
    const fetched: (Promise<ArrayBuffer> | undefined)[] = [];
    const fetchOf = (i: number) => (fetched[i] ??= parts.say(sentences[i]!));
    const finish = () => {
      if (mine !== turn) return;
      stop();
      done();
    };
    const play = async (i: number) => {
      if (mine !== turn) return;
      if (i >= sentences.length) {
        // Nothing to play yet: the next push resumes here, or the close ends the reading.
        if (closed) finish();
        else waiting = true;
        return;
      }
      at = i;
      let bytes: ArrayBuffer;
      try {
        bytes = await fetchOf(i);
      } catch {
        finish();
        return;
      }
      if (mine !== turn) return;
      if (i + 1 < sentences.length) fetchOf(i + 1).catch(() => {});
      stop();
      playing = urls.create(bytes);
      audio.onended = () => void play(i + 1);
      audio.onerror = finish;
      audio.src = playing;
      try {
        await audio.play();
      } catch {
        finish();
      }
    };
    return {
      push(text: string) {
        if (mine !== turn || closed) return;
        const more = chunkForSpeech(text, Number.POSITIVE_INFINITY);
        if (more.length === 0) return;
        const first = sentences.length;
        sentences.push(...more);
        if (waiting) {
          waiting = false;
          void play(first);
        } else if (first > 0 && at === first - 1) {
          // The one playing is the last so far: have the next one's audio ready before it ends.
          fetchOf(first).catch(() => {});
        }
      },
      end() {
        if (mine !== turn || closed) return;
        closed = true;
        if (waiting) {
          waiting = false;
          finish();
        }
      },
      start() {
        void play(0);
      },
    };
  };

  return {
    speak(text, done) {
      const one = reading(done);
      one.push(text);
      one.end();
      one.start();
    },
    stream(done) {
      const many = reading(done);
      many.start();
      return { push: many.push, end: many.end };
    },
    cancel() {
      turn += 1;
      stop();
    },
    prime() {
      // Inside the tap: silence on the element, so iOS lets the answer play seconds later.
      audio.src = silentWav();
      void Promise.resolve(audio.play()).catch(() => {});
    },
  };
}

/** The element and the URLs the browser has, or null where it has neither (a test under Node). */
export function browserPollyParts(
  scope: object,
  say: (text: string) => Promise<ArrayBuffer>,
): PollyParts | null {
  const { Audio, URL: Urls } = scope as {
    Audio?: new () => AudioLike;
    URL?: { createObjectURL?(blob: Blob): string; revokeObjectURL?(url: string): void };
  };
  if (typeof Audio !== 'function' || !Urls?.createObjectURL || !Urls.revokeObjectURL) return null;
  return {
    say,
    audio: new Audio(),
    urls: {
      create: (bytes) => Urls.createObjectURL!(new Blob([bytes], { type: 'audio/mpeg' })),
      revoke: (url) => Urls.revokeObjectURL!(url),
    },
  };
}
